import { _post } from "../../../libraries/fetch";
import { ensureProductionApiToken } from "../../../libraries/auth";
import { getVisitorReportURL } from "../../../libraries/apiUrl";
import { getMonthDateRange } from "./SalesOrders";

export function normalizeVisitorReportRows(response) {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.response?.data)) return response.response.data;
  if (Array.isArray(response?.response)) return response.response;
  if (Array.isArray(response?.result?.data)) return response.result.data;
  if (Array.isArray(response?.result)) return response.result;
  if (Array.isArray(response?.visitors)) return response.visitors;
  return [];
}

export function getVisitorReportSummary(response) {
  if (!response || typeof response !== "object") return null;
  if (response.summary && typeof response.summary === "object") return response.summary;
  if (response.response?.summary && typeof response.response.summary === "object") {
    return response.response.summary;
  }
  if (response.result?.summary && typeof response.result.summary === "object") {
    return response.result.summary;
  }
  return null;
}

function toDayStamp(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    return `${match[1]}-${match[2]}-${match[3]}`;
  }

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Keep only entries whose date falls in [from, to] (YYYY-MM-DD inclusive). */
export function filterEntriesByDateRange(rows, from, to) {
  if (!Array.isArray(rows) || !rows.length) return [];

  return rows.filter((row) => {
    if (row?.isDelete) return false;
    const day = toDayStamp(row?.date ?? row?.invoiceDate ?? row?.createdAt);
    if (!day) return false;
    return day >= from && day <= to;
  });
}

/** Match visitor report: total collected fee = sum of pay for month entries only. */
export function sumDayPassSalesTotal(rows) {
  let total = 0;
  for (const row of rows) {
    if (row?.isDelete) continue;
    total += Number.parseInt(String(row?.pay ?? 0), 10) || 0;
  }
  return total;
}

function isEmptyReportError(response) {
  const status = response?.status;
  const message = String(response?.message || response?.error || "");
  return (
    status === 404 ||
    /not found|no (visitor|day.?pass)|no records|empty/i.test(message)
  );
}

export async function fetchDayPassSales(branchId, month, year) {
  const resolvedBranchId =
    typeof branchId === "object" && branchId !== null
      ? String(branchId._id || branchId.id || "").trim()
      : String(branchId || "").trim();

  if (!resolvedBranchId) {
    return { rows: [], total: 0, from: "", to: "", error: "Waiting for branch..." };
  }

  await ensureProductionApiToken();

  // Selected month start / end dates for the report payload
  const { from, to } = getMonthDateRange(month, year);
  const payload = {
    from,
    to,
    conductedBy: "All",
    enteredBy: "All",
    saleType: "All"
  };

  const response = await _post(getVisitorReportURL(resolvedBranchId), payload);

  if (response?.success === false) {
    if (isEmptyReportError(response)) {
      return { rows: [], total: 0, from, to, error: "" };
    }

    return {
      rows: [],
      total: 0,
      from,
      to,
      error: response?.message || "Failed to load day pass sales"
    };
  }

  const allRows = normalizeVisitorReportRows(response);
  // Client-side guard: only keep entries inside selected month start/end
  const monthRows = filterEntriesByDateRange(allRows, from, to);
  const summary = getVisitorReportSummary(response);

  const rowsTotal = sumDayPassSalesTotal(monthRows);
  const summaryTotal = Number(summary?.visitorFee);
  // Summary is also built from [from, to] on the server and includes every fee
  // detail (response rows can collapse per visitor). Use the fuller month total.
  const total =
    Number.isFinite(summaryTotal) && summaryTotal >= rowsTotal
      ? summaryTotal
      : rowsTotal;

  return {
    rows: monthRows,
    total,
    from,
    to,
    summary,
    error: ""
  };
}
